import { useState } from 'react';
import { Button } from '@appwin/ui';
import { api, ApiError, type CreateAppInput } from './api.js';

/**
 * App creation form.
 *
 * The app id is permanent once created (it is the catalogue URL and the feed
 * key), so the prefix is derived from the signed-in developer and the user only
 * chooses the last segment.
 */
export function NewAppDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateAppInput>({
    id: '',
    name: '',
    category: 'utilities',
    summary: { en: '' },
    description: { en: '' },
    homepageUrl: '',
    license: '',
  });
  // Fixed English text: the catalogue falls back to it for every locale.
  const [summaryEn, setSummaryEn] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof CreateAppInput>(key: K, value: CreateAppInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: CreateAppInput = {
        ...form,
        // The server enforces the developer prefix; the portal only helps by
        // building the same value the user sees.
        id: form.id.trim(),
        summary: { en: summaryEn.trim() },
        description: { en: descriptionEn.trim() },
        homepageUrl: form.homepageUrl?.trim() || undefined,
        license: form.license?.trim() || undefined,
      };
      await api.createApp(payload);
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не удалось создать приложение');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    form.id.includes('/') &&
    form.name.trim().length > 0 &&
    summaryEn.trim().length > 0 &&
    descriptionEn.trim().length > 0;

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold">Новое приложение</h2>

        <div className="mt-4 space-y-3">
          <Field
            label="Идентификатор"
            hint="Формат: ваш-ник/название. После создания изменить нельзя."
          >
            <div className="flex gap-2">
              <input
                value={form.id}
                onChange={(event) => update('id', event.target.value)}
                placeholder="мой-ник/мое-приложение"
                className="h-10 flex-1 rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </div>
          </Field>

          <Field label="Название">
            <input
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>

          <Field label="Краткое описание (английский)">
            <input
              value={summaryEn}
              onChange={(event) => setSummaryEn(event.target.value)}
              maxLength={200}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
            />
          </Field>

          <Field label="Полное описание (английский)">
            <textarea
              value={descriptionEn}
              onChange={(event) => setDescriptionEn(event.target.value)}
              rows={4}
              className="w-full rounded-lg border border-border bg-bg p-3 text-sm"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Лицензия (SPDX)">
              <input
                value={form.license ?? ''}
                onChange={(event) => update('license', event.target.value)}
                placeholder="MIT"
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
            <Field label="Сайт">
              <input
                value={form.homepageUrl ?? ''}
                onChange={(event) => update('homepageUrl', event.target.value)}
                placeholder="https://"
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm"
              />
            </Field>
          </div>
        </div>

        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={busy || !valid}>
            {busy ? 'Создание…' : 'Создать'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null}
    </label>
  );
}
